import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  TablePagination,
  Drawer,
  Breadcrumbs,
  Chip,
} from "@mui/material";
import { useSelector } from "react-redux";
import { customGetAPI } from "services/utils/CustomAPI";
import { Loader } from "components/Loader";
import { showToast } from "services/utils/Status";
import {
  CustomButton,
  InfoMessage,
} from "components/CustomComponents/CustomButton";
import {
  DateRange,
  ModeStandby,
  AccessTime,
  Assessment,
} from "@mui/icons-material";
import { CustomFilter } from "components/CustomComponents/CustomFilter";
import { themes, tableRowSize, theme, drawerStyle } from "services/constants";
import { ThemeProvider } from "@mui/material/styles";
import EODCreatePanel from "./EODCreatePanel";
import EODViewPanel from "./EODViewPanel";
import { FilterProvider } from "pages/FilterContext";

const EODReportHeaders = [
  {
    key: "report_date",
    name: "Date",
    type: "date",
    icon: <DateRange fontSize="small" />,
    showBetweenOperator: true,
    dateRangeFields: {
      fromField: "report_date",
      toField: "report_date",
    },
  },
  {
    key: "total_worked_hours",
    name: "Hours",
    type: "time",
    icon: <AccessTime fontSize="small" />,
    showBetweenOperator: false,
  },
  {
    key: "achieved_tasks_summary",
    name: "Tasks Achieved",
    type: "string",
    icon: <Assessment fontSize="small" />,
  },
  {
    key: "planned_tasks_summary",
    name: "Planned for Next",
    type: "string",
    icon: <Assessment fontSize="small" />,
  },
  {
    key: "blockers_notes",
    name: "Blockers",
    type: "string",
    icon: <Assessment fontSize="small" />,
  },
  {
    key: "status",
    name: "Status",
    type: "string",
    icon: <ModeStandby fontSize="small" />,
  },
];

const EODReporContent = () => {
  const { domain, userDetails } = useSelector((state) => state.tokenReducer);
  console.log(userDetails);

  // --- State ---
  const [hasProjectAccess, setHasProjectAccess] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [eodReports, setEodReports] = useState([]);
  const [filteredReports, setFilteredReports] = useState([]);
  const [loading, setLoading] = useState(true);

  // Store selected Report ID for the View Panel API call
  const [selectedReportId, setSelectedReportId] = useState(null);

  const containerRef = useRef(null);
  const [isCreatePanelOpen, setIsCreatePanelOpen] = useState(false);
  const [isViewPanelOpen, setIsViewPanelOpen] = useState(false);

  // --- 1. Fetch User Configuration (Project Access) ---
  useEffect(() => {
    const fetchConfig = async () => {
      if (!userDetails?.id) return;
      try {
        const res = await customGetAPI(
          `${domain}eod-reports/eod_config/?user_id=${userDetails.id}`,
        );
        if (res.ok) {
          const data = res.responseDetails || res.data;
          // create boolean from response
          setHasProjectAccess(!!data?.has_project_access);
        }
      } catch (err) {
        console.error("Error fetching config", err);
      }
    };
    fetchConfig();
  }, [domain, userDetails]);

  // --- 2. Fetch Reports List ---
  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const response = await customGetAPI(
        `${domain}eod-reports/?selected_user_id=${userDetails?.id}`,
      );
      if (response.ok) {
        const reports = Array.isArray(response.responseDetails)
          ? response.responseDetails
          : response.responseDetails?.data || [];
        setEodReports(reports);
        setFilteredReports(reports);
      }
    } catch (error) {
      console.error("Fetch error", error);
      showToast("error", "Failed to load EOD reports");
    } finally {
      setLoading(false);
    }
  }, [domain, userDetails]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  // --- Handlers ---
  const reCallApi = (shouldRefetch) => {
    if (shouldRefetch) fetchReports();
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilteredDataChange = useCallback(
    (filteredData) => {
      setFilteredReports(filteredData);
      if (page * rowsPerPage >= filteredData.length) setPage(0);
    },
    [page, rowsPerPage],
  );

  const handleCreatePanel = () => setIsCreatePanelOpen(true);
  const handleCreateClosePanel = () => setIsCreatePanelOpen(false);

  // Open View Panel: Set ID so Child component can fetch full details
  const handleViewPanel = (row) => {
    if (row?.id) {
      setSelectedReportId(row.id);
      setIsViewPanelOpen(true);
    }
  };

  const handleViewClosePanel = () => {
    setIsViewPanelOpen(false);
    setSelectedReportId(null);
  };

  // --- UI Helpers ---
  // Updated: Uses 'body2' (standard text size) instead of 'caption'
  const renderTaskList = (tasks) => {
    if (!tasks) return "-";
    const list = Array.isArray(tasks) ? tasks : [tasks];
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
          alignItems: "flex-start",
        }}
      >
        {list.map((task, index) => (
          <Typography
            key={index}
            variant="body2" // Increased font size (same as header)
            sx={{ textAlign: "left", lineHeight: 1.4, fontSize: "0.875rem" }}
          >
            • {task}
          </Typography>
        ))}
      </Box>
    );
  };

  const getStatusStyles = (status) => {
    switch (status) {
      case "Approved":
        return {
          bgcolor: "#e8f5e9",
          color: "#2e7d32",
        };
      case "Submitted":
        return {
          bgcolor: "#e3f2fd",
          color: "#1565c0",
        };
      case "Reopened":
        return {
          bgcolor: "#ffebee",
          color: "#c62828",
        };
      case "Weekoff":
        return {
          bgcolor: "#e0e0e0",
          color: "#0e0d0d",
        };
      case "Holiday":
        return {
          bgcolor: "#9D97FA",
          color: "#000000",
        };
      case "Leave":
        return {
          bgcolor: "#D3A50E",
          color: "#000000",
        };
      case "Draft":
      default:
        return {
          bgcolor: "#fff3e0",
          color: "#ef6c00",
        };
    }
  };

  return (
    <ThemeProvider theme={theme}>
      {loading ? (
        <Loader />
      ) : (
        <React.Fragment>
          <Box>
            {/* Header Section - Added Margin Top */}
            <Box sx={{ mb: 3, mt: 2 }}>
              <Breadcrumbs aria-label="breadcrumb" separator="›" sx={{ mb: 2 }}>
                <Typography>My Zone</Typography>
                <Typography color={themes?.blueColor}>EOD Report</Typography>
              </Breadcrumbs>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                    EOD Report
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Track your daily work progress and productivity
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Filter and Table */}
            <Paper sx={{ width: "100%" }}>
              <CustomFilter
                title="EOD Reports"
                filterHeaders={EODReportHeaders}
                data={eodReports}
                filteredData={filteredReports}
                onFilteredDataChange={handleFilteredDataChange}
                screenAccess={true}
                newButtonComponent={
                  <CustomButton
                    actionFuntion={handleCreatePanel}
                    actionName="New"
                    typeName="button"
                  />
                }
              />

              <Box sx={{ ml: 2, mr: 2, mb: 2 }}>
                <TableContainer ref={containerRef} sx={{ maxHeight: "60vh" }}>
                  <Table className="border">
                    <TableHead
                      sx={{
                        backgroundColor: themes.primary,
                        position: "sticky",
                        top: -1,
                        zIndex: 10,
                      }}
                    >
                      <TableRow sx={tableRowSize}>
                        {EODReportHeaders.map((header) => (
                          <TableCell
                            key={header.key}
                            align="center"
                            sx={{
                              color: themes.headLine,
                              fontWeight: "bold",
                              border: "1px solid #ddd",
                            }}
                          >
                            {header.name}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredReports?.length === 0 ? (
                        <InfoMessage dataLength={eodReports} />
                      ) : (
                        filteredReports
                          ?.slice(
                            page * rowsPerPage,
                            page * rowsPerPage + rowsPerPage,
                          )
                          ?.map((row) => {
                            const clickable =
                              row.status !== "Weekoff" &&
                              row.status !== "Holiday" &&
                              row.status !== "Leave";
                            const commonCellProps = {
                              border: "1px solid #ddd",
                              cursor: clickable ? "pointer" : "default",
                              opacity: clickable ? 1 : 0.6,
                            };
                            return (
                              <TableRow
                                key={row.id}
                                sx={{
                                  ...tableRowSize,
                                  "&:hover": clickable
                                    ? { backgroundColor: "#f5f5f5" }
                                    : {},
                                }}
                              >
                                <TableCell
                                  align="left"
                                  sx={commonCellProps}
                                  onClick={() =>
                                    clickable && handleViewPanel(row)
                                  }
                                >
                                  {row.report_date}
                                </TableCell>
                                <TableCell
                                  align="left"
                                  sx={commonCellProps}
                                  onClick={() =>
                                    clickable && handleViewPanel(row)
                                  }
                                >
                                  {row.total_worked_hours || "-"}
                                </TableCell>
                                <TableCell
                                  align="left"
                                  sx={{
                                    ...commonCellProps,
                                    maxWidth: 350,
                                  }}
                                  onClick={() =>
                                    clickable && handleViewPanel(row)
                                  }
                                >
                                  {renderTaskList(row.achieved_tasks_summary)}
                                </TableCell>
                                <TableCell
                                  align="left"
                                  sx={{
                                    ...commonCellProps,
                                    maxWidth: 350,
                                  }}
                                  onClick={() =>
                                    clickable && handleViewPanel(row)
                                  }
                                >
                                  {renderTaskList(row.planned_tasks_summary)}
                                </TableCell>
                                <TableCell
                                  align="left"
                                  sx={commonCellProps}
                                  onClick={() =>
                                    clickable && handleViewPanel(row)
                                  }
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{ whiteSpace: "pre-wrap" }}
                                  >
                                    {row.blockers_notes || "-"}
                                  </Typography>
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={commonCellProps}
                                  onClick={() =>
                                    clickable && handleViewPanel(row)
                                  }
                                >
                                  {/* Status Chip */}
                                  <Chip
                                    label={row.status || "Draft"}
                                    size="small"
                                    sx={{
                                      ...getStatusStyles(row.status),
                                      fontWeight: 600,
                                      minWidth: 80,
                                    }}
                                  />
                                </TableCell>
                              </TableRow>
                            );
                          })
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
              <Box sx={{ mt: 1 }}>
                {filteredReports?.length > 25 && (
                  <TablePagination
                    className="custom-pagination"
                    component="div"
                    rowsPerPageOptions={[25, 50, 75, 100]}
                    count={filteredReports?.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                  />
                )}
              </Box>
            </Paper>
          </Box>

          {/* Drawer: Passes ID and Access props correctly */}
          <Drawer
            anchor="right"
            open={isCreatePanelOpen || isViewPanelOpen}
            onClose={(event, reason) => {
              if (reason === "backdropClick" || reason === "escapeKeyDown")
                return;
              isCreatePanelOpen
                ? handleCreateClosePanel()
                : handleViewClosePanel();
            }}
            sx={drawerStyle}
          >
            {isCreatePanelOpen ? (
              <EODCreatePanel
                onClose={handleCreateClosePanel}
                reCallApi={reCallApi}
                hasProjectAccess={hasProjectAccess}
              />
            ) : isViewPanelOpen ? (
              <EODViewPanel
                onClose={handleViewClosePanel}
                reportId={selectedReportId}
                reCallApi={reCallApi}
                hasProjectAccess={hasProjectAccess}
              />
            ) : null}
          </Drawer>
        </React.Fragment>
      )}
    </ThemeProvider>
  );
};

export const EODReports = () => {
  const { domain, userDetails, userScreenAccess } = useSelector(
    (state) => state.tokenReducer,
  );

  const eodModule = userScreenAccess?.module_details.find(
    (mod) => mod.module_name === "EOD Report",
  );

  const subModuleId = eodModule?.submodules?.find(
    (sub) => sub.sub_module_name === "EOD Report",
  )?.sub_module_id;

  return (
    <FilterProvider
      domain={domain}
      userDetails={userDetails}
      subModuleId={subModuleId}
    >
      <EODReporContent />
    </FilterProvider>
  );
};

export default EODReports;
